(() => {
  const CACHE_KEY = 'gos-format-products-v3';
  const normalize = value => String(value || '').toLowerCase().replace(/[’']/g, "'").replace(/\s+/g, ' ').trim();
  const tags = product => Array.isArray(product.tags) ? product.tags.map(normalize) : String(product.tags || '').split(',').map(normalize);
  const formatOf = product => {
    const title = String(product.title || '');
    const productTags = tags(product);
    if (/\brefill\b/i.test(title) || productTags.includes('refill')) return 'refill';
    if (/\bsample\b/i.test(title) || productTags.includes('sample')) return 'sample';
    return 'bottle';
  };
  const baseTitle = value => String(value || '')
    .replace(/\s*,?\s*refill\s*(?:\([^)]*\))?\s*$/i, '')
    .replace(/\s*[-–—,]?\s*(?:sample|sample size)\s*(?:\([^)]*\))?\s*$/i, '')
    .replace(/\s*\((?:sample|sample size)\)\s*$/i, '')
    .replace(/\s*,\s*$/, '')
    .trim();
  const baseKey = value => normalize(baseTitle(value)).replace(/[^a-z0-9]+/g, ' ').trim();
  const escapeHTML = value => String(value || '').replace(/[&<>'"]/g, character => ({
    '&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'
  })[character]);

  const readCache = () => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      return cached && Array.isArray(cached.products) && Date.now() - cached.savedAt < 15 * 60 * 1000 ? cached.products : null;
    } catch (_) { return null; }
  };
  const writeCache = products => {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), products })); } catch (_) {}
  };
  const fetchPage = page => fetch('/products.json?limit=250&page=' + page, {
    credentials: 'same-origin',
    cache: 'default',
    signal: AbortSignal.timeout(10000)
  }).then(response => response.ok ? response.json() : Promise.reject(new Error('Product request failed')))
    .then(payload => Array.isArray(payload.products) ? payload.products : []);

  let catalogPromise;
  const loadCatalog = () => {
    if (catalogPromise) return catalogPromise;
    const cached = readCache();
    if (cached) return (catalogPromise = Promise.resolve(cached));
    catalogPromise = Promise.all([fetchPage(1), fetchPage(2)]).then(async pages => {
      const products = pages.flat();
      if (pages[1].length >= 250) {
        for (let page = 3; page <= 6; page += 1) {
          const batch = await fetchPage(page);
          products.push(...batch);
          if (batch.length < 250) break;
        }
      }
      const unique = [...new Map(products.filter(product => product && product.handle).map(product => [product.handle, product])).values()];
      writeCache(unique);
      return unique;
    });
    return catalogPromise;
  };

  const rank = { bottle: 0, refill: 1, sample: 2 };
  const label = format => format === 'refill' ? 'Refill' : format.charAt(0).toUpperCase() + format.slice(1);

  const initialize = async root => {
    if (!root || root.dataset.gosReady === 'true') return;
    root.dataset.gosReady = 'true';
    const grid = root.querySelector('[data-gos-format-grid]');
    const currentNode = root.querySelector('[data-gos-current-product]');
    if (!grid || !currentNode) return;
    let currentProduct;
    try { currentProduct = JSON.parse(currentNode.textContent || '{}'); } catch (_) { currentProduct = {}; }
    const currentKey = baseKey(currentProduct.title || document.querySelector('.product__title')?.textContent);
    if (!currentKey) { root.classList.remove('is-discovering'); return; }
    if (root.dataset.catalogMapped === 'true') {
      root.classList.remove('is-discovering');
      return;
    }

    try {
      const cached = readCache();
      const products = cached || await loadCatalog();
      const byFormat = new Map();
      const existingFormats = new Set([...grid.querySelectorAll('a[data-gos-format]')].map(link => link.dataset.gosFormat));
      products.filter(product => baseKey(product.title) === currentKey).forEach(product => {
        const format = formatOf(product);
        const existing = byFormat.get(format);
        if (!existing || product.handle === root.dataset.currentHandle) byFormat.set(format, product);
      });
      if (currentProduct.handle && ![...byFormat.values()].some(product => product.handle === currentProduct.handle)) {
        byFormat.set(formatOf(currentProduct), currentProduct);
      }
      for (const [format, product] of [...byFormat.entries()].sort((a, b) => rank[a[0]] - rank[b[0]])) {
        if (existingFormats.has(format)) continue;
        const placeholder = grid.querySelector('[data-gos-format="' + format + '"]');
        if (!placeholder) continue;
        const link = document.createElement('a');
        const isCurrent = product.handle === root.dataset.currentHandle;
        link.className = 'gos-format-pill' + (isCurrent ? ' is-current' : '');
        link.dataset.gosFormat = format;
        link.dataset.gosHandle = product.handle;
        link.href = (root.dataset.productsRoot || '/products/') + product.handle;
        if (isCurrent) link.setAttribute('aria-current', 'page');
        const name = document.createElement('span');
        name.className = 'gos-format-choice__name';
        name.textContent = root.dataset['label' + format.charAt(0).toUpperCase() + format.slice(1)] || label(format);
        link.appendChild(name);
        if (isCurrent) {
          const selected = document.createElement('span');
          selected.className = 'gos-format-choice__selected';
          selected.textContent = root.dataset.labelSelected || 'Selected';
          name.appendChild(selected);
        }
        const packageTitle = product.variants?.find(variant => variant.available)?.title || product.variants?.[0]?.title;
        if (packageTitle && packageTitle !== 'Default Title') {
          const pack = document.createElement('span');
          pack.className = 'gos-format-choice__package';
          pack.textContent = packageTitle;
          link.appendChild(pack);
        }
        placeholder.replaceWith(link);
      }
      root.classList.remove('is-discovering');
    } catch (_) {
      root.classList.remove('is-discovering');
    }
  };

  const prefetched = new Set();
  const prefetch = event => {
    const link = event.target.closest?.('[data-gos-format-selector] a:not([aria-current])');
    if (!link || prefetched.has(link.href)) return;
    const connection = navigator.connection;
    if (connection?.saveData || /2g/.test(connection?.effectiveType || '')) return;
    prefetched.add(link.href);
    const resource = document.createElement('link');
    resource.rel = 'prefetch';
    resource.href = link.href;
    document.head.appendChild(resource);
  };
  document.addEventListener('pointerover', prefetch, { passive: true });
  document.addEventListener('focusin', prefetch);

  const initializeAll = () => document.querySelectorAll('[data-gos-format-selector]').forEach(initialize);
  document.addEventListener('DOMContentLoaded', initializeAll);
  document.addEventListener('shopify:section:load', initializeAll);
  initializeAll();
})();
